<script setup>
/**
 * The AI actions this conversation is holding, and the two buttons that resolve them.
 *
 * A held action is a tool call the governed gate refused to run unattended: the row exists
 * because a human has to decide. So the arguments are shown exactly as the API sends them -
 * `tool_args` is the jsonb the model asked for, and the index payload does not redact it.
 * Reshaping or hiding a key here would show the approver something other than what approving
 * actually runs, which is the one thing this card must never do.
 *
 * The list is owned by the parent: it fetches, filters to this conversation, and refetches on
 * `decided`. That keeps the section from rendering an empty accordion header, since the parent
 * already knows the count before the card mounts.
 */
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  approvals: { type: Array, required: true },
});

const emit = defineEmits(['decided']);

const store = useStore();
const { t } = useI18n();

const decidingId = ref(null);

// Strings stay unquoted so an argument reads as the value it is; everything else is shown as
// the JSON it was stored as, nesting included.
const formatValue = value =>
  typeof value === 'string' ? value : JSON.stringify(value);

const decide = async (approval, action) => {
  const key = action.toUpperCase();
  decidingId.value = approval.id;
  try {
    await store.dispatch(`captainActionApprovals/${action}`, approval.id);
    useAlert(t(`CONVERSATION_SIDEBAR.AI_ACTION_APPROVALS.${key}_SUCCESS`));
    emit('decided');
  } catch (error) {
    useAlert(
      error.message ||
        t(`CONVERSATION_SIDEBAR.AI_ACTION_APPROVALS.${key}_ERROR`)
    );
  } finally {
    decidingId.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col gap-2 px-4">
    <div
      v-for="approval in approvals"
      :key="approval.id"
      class="p-3 border rounded-lg border-n-weak bg-n-solid-1"
    >
      <div class="flex items-center gap-2">
        <span
          class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
        >
          {{ t('CONVERSATION_SIDEBAR.AI_ACTION_APPROVALS.PENDING') }}
        </span>
        <span class="font-mono text-xs truncate text-n-slate-11">
          {{ approval.tool_name }}
        </span>
      </div>

      <p class="mt-1 text-xs text-n-slate-10">
        {{
          t('CONVERSATION_SIDEBAR.AI_ACTION_APPROVALS.REQUESTED_AT', {
            time: dynamicTime(approval.created_at),
          })
        }}
      </p>

      <dl class="flex flex-col gap-1 mt-2">
        <div
          v-for="(value, key) in approval.tool_args"
          :key="key"
          class="flex gap-2 text-xs"
        >
          <dt class="flex-shrink-0 text-n-slate-10">{{ key }}</dt>
          <dd
            class="min-w-0 font-mono break-words [overflow-wrap:anywhere] text-n-slate-12"
          >
            {{ formatValue(value) }}
          </dd>
        </div>
      </dl>

      <div class="flex gap-2 mt-3">
        <Button
          xs
          solid
          blue
          :label="t('CONVERSATION_SIDEBAR.AI_ACTION_APPROVALS.APPROVE')"
          :is-loading="decidingId === approval.id"
          :disabled="decidingId === approval.id"
          @click="decide(approval, 'approve')"
        />
        <Button
          xs
          faded
          ruby
          :label="t('CONVERSATION_SIDEBAR.AI_ACTION_APPROVALS.REJECT')"
          :disabled="decidingId === approval.id"
          @click="decide(approval, 'reject')"
        />
      </div>
    </div>
  </div>
</template>
